import type { SysmlApiAdapter } from './api-adapter.js';
import { parseSysML } from '@arctek/sysml-core';
import { STORAGE_KEYS, DEFAULT_SYSML_DOCUMENT, TOASTER_SYSML_DOCUMENT } from '../utils/constants.js';
import type {
  SysmlApiConfig,
  SysMLDocument,
  FolderNode,
  ChatResponse,
  ChatPhase,
  ModelContext,
  QualityScore,
  QualityFeedback,
  QualityIssue,
  RequirementQualityScore,
  TraceabilityMatrix,
  TraceabilityEntry,
  GenerateResponse,
  SysmlSuggestedOutput,
  SysmlChatbotModel,
  SysmlChatbotModelSummary,
  SysmlModelState,
  SysmlModelPart,
  SysmlModelBehavior,
  SysmlModelRequirement,
  SysmlModelRisk,
  SysmlModelVerificationCase,
  UpdateModelStateResult,
  VersionHistoryEntry,
  ChatMessage,
} from './types.js';

// ---------------------------------------------------------------------------
// Mock API service — localStorage-backed, same interface as the real backend
//
// Enhanced with:
//   - Real SysML parsing via @arctek/sysml-core parseSysML()
//   - Intelligent suggestions based on model gaps
//   - INCOSE quality scoring for requirements
//   - Traceability & verification coverage calculations
//   - SysML code generation using builder templates
//   - Model CRUD with version history
// ---------------------------------------------------------------------------

function uuid(): string {
  return crypto.randomUUID();
}

function now(): string {
  return new Date().toISOString();
}

// ── Storage keys for chatbot models ───────────────────────────────────────

const MODELS_KEY = 'sysml:chatbot-models';

export class SysmlApi implements SysmlApiAdapter {
  private config: SysmlApiConfig;

  constructor(config: SysmlApiConfig) {
    this.config = config;
    this._ensureDefaults();
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Document CRUD
  // ══════════════════════════════════════════════════════════════════════════

  async listDocuments(): Promise<SysMLDocument[]> {
    return this._getDocuments();
  }

  async getDocument(id: string): Promise<SysMLDocument> {
    const docs = this._getDocuments();
    const doc = docs.find((d) => d.id === id);
    if (!doc) throw new Error(`Document not found: ${id}`);
    return doc;
  }

  async createDocument(name: string, content?: string): Promise<SysMLDocument> {
    const docs = this._getDocuments();
    const doc: SysMLDocument = {
      id: uuid(),
      name,
      content: content ?? '',
      folderId: null,
      version: 1,
      createdAt: now(),
      updatedAt: now(),
    };
    docs.push(doc);
    this._setDocuments(docs);
    return doc;
  }

  async updateDocument(id: string, patch: Partial<SysMLDocument>): Promise<SysMLDocument> {
    const docs = this._getDocuments();
    const idx = docs.findIndex((d) => d.id === id);
    if (idx === -1) throw new Error(`Document not found: ${id}`);
    docs[idx] = {
      ...docs[idx],
      ...patch,
      id, // prevent id override
      version: docs[idx].version + 1,
      updatedAt: now(),
    };
    this._setDocuments(docs);
    return docs[idx];
  }

  async deleteDocument(id: string): Promise<void> {
    const docs = this._getDocuments().filter((d) => d.id !== id);
    this._setDocuments(docs);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Folders
  // ══════════════════════════════════════════════════════════════════════════

  async getFolderTree(): Promise<FolderNode[]> {
    return this._getFolders();
  }

  async updateFolderTree(tree: FolderNode[]): Promise<void> {
    this._setFolders(tree);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Chatbot Model CRUD
  // ══════════════════════════════════════════════════════════════════════════

  async listModels(): Promise<SysmlChatbotModelSummary[]> {
    await this._delay(100);
    const models = this._getModels();
    return models.map((m) => ({
      id: m.id,
      name: m.name,
      version: m.version,
      updatedAt: m.updatedAt,
    }));
  }

  async getModel(id: string): Promise<SysmlChatbotModel> {
    await this._delay(100);
    const models = this._getModels();
    const model = models.find((m) => m.id === id);
    if (!model) throw new Error(`Model not found: ${id}`);
    return model;
  }

  async createModel(params: { name: string; intent?: string }): Promise<SysmlChatbotModel> {
    await this._delay(200);
    const models = this._getModels();
    const model: SysmlChatbotModel = {
      id: uuid(),
      name: params.name,
      state: {},
      recentConversation: [],
      contextSummary: params.intent ?? undefined,
      version: 1,
      versionHistory: [],
      createdAt: now(),
      updatedAt: now(),
    };
    models.push(model);
    this._setModels(models);
    return model;
  }

  async deleteModel(id: string): Promise<void> {
    await this._delay(100);
    const models = this._getModels().filter((m) => m.id !== id);
    this._setModels(models);
  }

  async updateModelState(
    modelId: string,
    state: SysmlModelState,
  ): Promise<UpdateModelStateResult> {
    await this._delay(150);
    const models = this._getModels();
    const idx = models.findIndex((m) => m.id === modelId);
    if (idx === -1) throw new Error(`Model not found: ${modelId}`);

    // Snapshot version history before update
    const oldModel = models[idx];
    const historyEntry: VersionHistoryEntry = {
      version: oldModel.version,
      timestamp: now(),
      description: `State update v${oldModel.version}`,
      state: JSON.parse(JSON.stringify(oldModel.state)),
    };

    const history = oldModel.versionHistory ?? [];
    // Keep last 20 versions
    if (history.length >= 20) history.shift();
    history.push(historyEntry);

    models[idx] = {
      ...oldModel,
      state,
      version: oldModel.version + 1,
      versionHistory: history,
      updatedAt: now(),
    };
    this._setModels(models);

    // Return quality feedback
    const quality = this._assessRequirementQuality(state);
    return { quality };
  }

  async revertToVersion(modelId: string, version: number): Promise<SysmlChatbotModel> {
    await this._delay(200);
    const models = this._getModels();
    const idx = models.findIndex((m) => m.id === modelId);
    if (idx === -1) throw new Error(`Model not found: ${modelId}`);

    const history = models[idx].versionHistory ?? [];
    const entry = history.find((h) => h.version === version);
    if (!entry) throw new Error(`Version ${version} not found`);

    models[idx] = {
      ...models[idx],
      state: JSON.parse(JSON.stringify(entry.state)),
      version: models[idx].version + 1,
      updatedAt: now(),
    };
    this._setModels(models);
    return models[idx];
  }

  async getHistory(
    modelId: string,
    limit = 10,
  ): Promise<VersionHistoryEntry[]> {
    await this._delay(100);
    const models = this._getModels();
    const model = models.find((m) => m.id === modelId);
    if (!model) throw new Error(`Model not found: ${modelId}`);
    return (model.versionHistory ?? []).slice(-limit);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Chatbot chat (mock AI)
  // ══════════════════════════════════════════════════════════════════════════

  async sendChatMessage(
    message: string,
    context: ModelContext,
    modelId?: string,
  ): Promise<ChatResponse> {
    await this._delay(300 + Math.random() * 400);
    return this._generateChatResponse(message, context, modelId);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Generate SysML
  // ══════════════════════════════════════════════════════════════════════════

  async generateSysML(modelId: string): Promise<GenerateResponse> {
    await this._delay(500 + Math.random() * 500);
    const models = this._getModels();
    const model = models.find((m) => m.id === modelId);
    if (!model) throw new Error(`Model not found: ${modelId}`);
    return this._generateSysMLFromState(model.state, model.name);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Quality scoring
  // ══════════════════════════════════════════════════════════════════════════

  async getQualityScore(context: ModelContext): Promise<QualityScore> {
    await this._delay(200);
    return this._generateQualityScore(context);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Internal: storage helpers
  // ══════════════════════════════════════════════════════════════════════════

  private _getDocuments(): SysMLDocument[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.DOCUMENTS);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private _setDocuments(docs: SysMLDocument[]): void {
    localStorage.setItem(STORAGE_KEYS.DOCUMENTS, JSON.stringify(docs));
  }

  private _getFolders(): FolderNode[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.FOLDERS);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private _setFolders(tree: FolderNode[]): void {
    localStorage.setItem(STORAGE_KEYS.FOLDERS, JSON.stringify(tree));
  }

  private _getModels(): SysmlChatbotModel[] {
    try {
      const raw = localStorage.getItem(MODELS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private _setModels(models: SysmlChatbotModel[]): void {
    localStorage.setItem(MODELS_KEY, JSON.stringify(models));
  }

  /** Seed default documents on first use; ensure toaster model for existing users */
  private _ensureDefaults(): void {
    const docs = this._getDocuments();
    if (docs.length === 0) {
      const defaultDoc: SysMLDocument = {
        id: uuid(),
        name: 'Vehicle Model',
        content: DEFAULT_SYSML_DOCUMENT,
        folderId: null,
        version: 1,
        createdAt: now(),
        updatedAt: now(),
      };
      const toasterDoc: SysMLDocument = {
        id: uuid(),
        name: 'Toaster Model',
        content: TOASTER_SYSML_DOCUMENT,
        folderId: null,
        version: 1,
        createdAt: now(),
        updatedAt: now(),
      };
      this._setDocuments([defaultDoc, toasterDoc]);
    } else if (!docs.some(d => d.name === 'Toaster Model')) {
      // Migration: add toaster for existing users who already have documents
      const toasterDoc: SysMLDocument = {
        id: uuid(),
        name: 'Toaster Model',
        content: TOASTER_SYSML_DOCUMENT,
        folderId: null,
        version: 1,
        createdAt: now(),
        updatedAt: now(),
      };
      this._setDocuments([...docs, toasterDoc]);
    }
  }

  private _delay(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms));
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Internal: parse source text with @arctek/sysml-core
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * Parse the source text using the real SysML parser and extract model
   * element counts. Falls back to regex counts if parsing fails.
   */
  private _analyzeSourceText(sourceText: string): {
    partCount: number;
    requirementCount: number;
    behaviorCount: number;
    verificationCount: number;
    portCount: number;
    connectionCount: number;
    constraintCount: number;
    errorCount: number;
    warningCount: number;
    partNames: string[];
    requirementNames: string[];
    behaviorNames: string[];
    verificationNames: string[];
    requirementTexts: Map<string, string>;
  } {
    const result = {
      partCount: 0,
      requirementCount: 0,
      behaviorCount: 0,
      verificationCount: 0,
      portCount: 0,
      connectionCount: 0,
      constraintCount: 0,
      errorCount: 0,
      warningCount: 0,
      partNames: [] as string[],
      requirementNames: [] as string[],
      behaviorNames: [] as string[],
      verificationNames: [] as string[],
      requirementTexts: new Map<string, string>(),
    };

    if (!sourceText.trim()) return result;

    try {
      const parseResult = parseSysML(sourceText, { allowPartialModel: true });

      result.errorCount = parseResult.diagnostics.filter(
        (d) => d.severity === 'error',
      ).length;
      result.warningCount = parseResult.diagnostics.filter(
        (d) => d.severity === 'warning',
      ).length;

      if (parseResult.model) {
        const m = parseResult.model;

        result.partCount = m.allPartDefs.length;
        result.partNames = m.allPartDefs.map((p) => p.name);

        result.requirementCount = m.allRequirementDefs.length;
        result.requirementNames = m.allRequirementDefs.map((r) => r.name);
        for (const req of m.allRequirementDefs) {
          if (req.text) {
            result.requirementTexts.set(req.name, req.text);
          }
        }

        result.behaviorCount =
          m.allActionDefs.length + m.allStateDefs.length;
        result.behaviorNames = [
          ...m.allActionDefs.map((a) => a.name),
          ...m.allStateDefs.map((s) => s.name),
        ];

        result.verificationCount = m.allVerificationDefs.length;
        result.verificationNames = m.allVerificationDefs.map((v) => v.name);

        result.portCount = m.allPortDefs.length + m.allPorts.length;
        result.connectionCount =
          m.allConnectionDefs.length + m.allConnections.length;
        result.constraintCount = m.allConstraintDefs.length;
      }
    } catch {
      // Fall back to regex counting if parser unavailable
      result.partCount = (sourceText.match(/part\s+def\s+\w+/g) || []).length;
      result.requirementCount = (sourceText.match(/requirement\s+def\s+\w+/g) || []).length;
      result.behaviorCount =
        (sourceText.match(/action\s+def\s+\w+/g) || []).length +
        (sourceText.match(/state\s+def\s+\w+/g) || []).length;
      result.verificationCount = (sourceText.match(/verification\s+def\s+\w+/g) || []).length;
      result.portCount = (sourceText.match(/port\s+def\s+\w+/g) || []).length;
    }

    return result;
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Internal: completeness scoring
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * Calculate completeness score: parts*10 + behaviors*20 + requirements*20 + risks*10
   * Max: 100
   */
  private _computeCompletenessFromState(state: SysmlModelState): number {
    const parts = state.parts?.length ?? 0;
    const behaviors = state.behaviors?.length ?? 0;
    const requirements = state.requirements?.length ?? 0;
    const risks = state.risks?.length ?? 0;
    return Math.min(100, parts * 10 + behaviors * 20 + requirements * 20 + risks * 10);
  }

  private _computeCompletenessFromContext(ctx: ModelContext): number {
    let score = 0;
    if (ctx.partCount > 0) score += Math.min(30, ctx.partCount * 10);
    if (ctx.requirementCount > 0) score += Math.min(30, ctx.requirementCount * 20);
    if (ctx.behaviorCount > 0) score += Math.min(20, ctx.behaviorCount * 20);
    if (ctx.verificationCount > 0) score += 10;
    if (ctx.riskCount > 0) score += 10;
    return Math.min(100, score);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Internal: INCOSE requirement quality assessment
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * Assess requirement quality based on INCOSE rules:
   * - Shall statement: requirement should use "shall"
   * - Atomic: requirement should describe one thing
   * - Measurable: requirement should have quantifiable criteria
   * - Unambiguous: avoid vague words like "adequate", "sufficient", "fast"
   * - Traceable: requirement should be traced to a part
   * - Verifiable: requirement should have a verification case
   */
  private _assessRequirementQuality(state: SysmlModelState): QualityFeedback {
    const requirementScores: Record<string, RequirementQualityScore> = {};
    const reqs = state.requirements ?? [];

    if (reqs.length === 0) {
      return { overallScore: 0, requirementScores: {} };
    }

    let totalScore = 0;

    for (const req of reqs) {
      const issues: RequirementQualityScore['issues'] = [];
      let score = 100;

      const text = (req.text ?? '').toLowerCase();

      // Rule 1: Uses "shall"
      if (!text.includes('shall')) {
        issues.push({
          rule: 'shall-statement',
          message: `"${req.name}" should use "shall" for mandatory requirements.`,
          severity: 'warning',
        });
        score -= 15;
      }

      // Rule 2: Atomic (not multiple "shall" or "and shall")
      const shallCount = (text.match(/\bshall\b/g) || []).length;
      if (shallCount > 1) {
        issues.push({
          rule: 'atomic',
          message: `"${req.name}" contains multiple "shall" statements. Split into atomic requirements.`,
          severity: 'warning',
        });
        score -= 10;
      }

      // Rule 3: Measurable (has numbers, units, or quantifiers)
      const hasNumbers = /\d/.test(text);
      const hasUnits =
        /\b(seconds?|minutes?|hours?|ms|hz|khz|mhz|kg|lb|meters?|feet|mph|km\/h|%|percent)\b/i.test(
          text,
        );
      if (!hasNumbers && !hasUnits) {
        issues.push({
          rule: 'measurable',
          message: `"${req.name}" lacks quantifiable criteria. Add specific values and units.`,
          severity: 'info',
        });
        score -= 10;
      }

      // Rule 4: Unambiguous (no vague words)
      const vagueWords = [
        'adequate',
        'sufficient',
        'fast',
        'slow',
        'efficient',
        'easy',
        'user-friendly',
        'reasonable',
        'appropriate',
        'good',
        'bad',
        'quickly',
        'some',
        'many',
        'few',
        'several',
        'etc',
        'and/or',
        'as needed',
      ];
      const foundVague = vagueWords.filter((w) => text.includes(w));
      if (foundVague.length > 0) {
        issues.push({
          rule: 'unambiguous',
          message: `"${req.name}" uses vague terms: ${foundVague.join(', ')}. Use precise language.`,
          severity: 'warning',
        });
        score -= foundVague.length * 5;
      }

      // Rule 5: Traceable (traced to at least one part)
      if (!req.tracedTo || req.tracedTo.length === 0) {
        issues.push({
          rule: 'traceable',
          message: `"${req.name}" is not traced to any system part.`,
          severity: 'info',
        });
        score -= 10;
      }

      // Rule 6: Verifiable (has at least one verification case)
      if (!req.verifiedBy || req.verifiedBy.length === 0) {
        issues.push({
          rule: 'verifiable',
          message: `"${req.name}" has no verification case assigned.`,
          severity: 'info',
        });
        score -= 10;
      }

      score = Math.max(0, Math.min(100, score));
      totalScore += score;

      requirementScores[req.name] = {
        name: req.name,
        score,
        issues,
      };
    }

    return {
      overallScore: Math.round(totalScore / reqs.length),
      requirementScores,
    };
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Internal: traceability computation
  // ══════════════════════════════════════════════════════════════════════════

  private _computeTraceability(state: SysmlModelState): TraceabilityMatrix {
    const reqs = state.requirements ?? [];
    const verifications = state.verificationCases ?? [];

    if (reqs.length === 0) {
      return {
        totalRequirements: 0,
        tracedRequirements: 0,
        untracedRequirements: 0,
        verificationCoverage: 0,
        entries: [],
      };
    }

    const entries: TraceabilityEntry[] = [];
    let tracedCount = 0;
    let verifiedCount = 0;

    for (const req of reqs) {
      const tracedToParts = req.tracedTo ?? [];
      const verifiedBy = req.verifiedBy ?? [];

      // Also check verification cases that reference this requirement
      const additionalVerifiers = verifications
        .filter((v) => v.verifies?.includes(req.name))
        .map((v) => v.name)
        .filter((n) => !verifiedBy.includes(n));

      const allVerifiers = [...verifiedBy, ...additionalVerifiers];

      const isTraced = tracedToParts.length > 0;
      const isVerified = allVerifiers.length > 0;

      if (isTraced) tracedCount++;
      if (isVerified) verifiedCount++;

      let status: TraceabilityEntry['status'] = 'untraced';
      if (isTraced && isVerified) status = 'fully-traced';
      else if (isTraced || isVerified) status = 'partially-traced';

      entries.push({
        requirementName: req.name,
        tracedToParts,
        verifiedBy: allVerifiers,
        status,
      });
    }

    return {
      totalRequirements: reqs.length,
      tracedRequirements: tracedCount,
      untracedRequirements: reqs.length - tracedCount,
      verificationCoverage:
        reqs.length > 0 ? Math.round((verifiedCount / reqs.length) * 100) : 0,
      entries,
    };
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Internal: phase determination
  // ══════════════════════════════════════════════════════════════════════════

  private _determinePhase(
    ctx: ModelContext,
    state?: SysmlModelState,
  ): ChatPhase {
    const completeness = state
      ? this._computeCompletenessFromState(state)
      : this._computeCompletenessFromContext(ctx);

    // Ready once we have a reasonable model (>=70 completeness) or at least
    // requirements + parts
    if (completeness >= 70) return 'ready';

    const hasReqs = (state?.requirements?.length ?? ctx.requirementCount) > 0;
    const hasParts = (state?.parts?.length ?? ctx.partCount) > 0;
    if (hasReqs && hasParts) return 'ready';

    return 'exploring';
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Internal: mock chat response generation
  // ══════════════════════════════════════════════════════════════════════════

  private _generateChatResponse(
    message: string,
    ctx: ModelContext,
    modelId?: string,
  ): ChatResponse {
    const lowerMsg = message.toLowerCase();

    // Analyze source text with the real parser
    const analysis = this._analyzeSourceText(ctx.sourceText);

    // Merge analysis counts into context for accurate completeness
    const mergedCtx: ModelContext = {
      ...ctx,
      partCount: Math.max(ctx.partCount, analysis.partCount),
      requirementCount: Math.max(ctx.requirementCount, analysis.requirementCount),
      behaviorCount: Math.max(ctx.behaviorCount, analysis.behaviorCount),
      verificationCount: Math.max(ctx.verificationCount, analysis.verificationCount),
      portCount: Math.max(ctx.portCount, analysis.portCount),
      errorCount: analysis.errorCount,
      warningCount: analysis.warningCount,
    };

    // Get model state if available
    let modelState: SysmlModelState | undefined;
    if (modelId) {
      try {
        const models = this._getModels();
        const model = models.find((m) => m.id === modelId);
        if (model) modelState = model.state;
      } catch {
        // Ignore
      }
    }

    // Build model state from parsed source if no model state
    if (!modelState || Object.keys(modelState).length === 0) {
      modelState = this._buildModelStateFromAnalysis(analysis);
    }

    const phase = this._determinePhase(mergedCtx, modelState);
    const completenessScore = modelState
      ? this._computeCompletenessFromState(modelState)
      : this._computeCompletenessFromContext(mergedCtx);

    // Determine response based on intent
    if (
      lowerMsg.includes('missing') ||
      lowerMsg.includes('improve') ||
      lowerMsg.includes('suggest') ||
      lowerMsg.includes('what should') ||
      lowerMsg.includes('help me')
    ) {
      return this._handleSuggestionsRequest(mergedCtx, analysis, modelState, phase, completenessScore);
    }

    if (
      lowerMsg.includes('generate') ||
      lowerMsg.includes('create') ||
      lowerMsg.includes('add')
    ) {
      return this._handleGenerateRequest(lowerMsg, mergedCtx, analysis, modelState, phase, completenessScore);
    }

    if (
      lowerMsg.includes('quality') ||
      lowerMsg.includes('score') ||
      lowerMsg.includes('health') ||
      lowerMsg.includes('incose')
    ) {
      return this._handleQualityRequest(mergedCtx, analysis, modelState, phase, completenessScore);
    }

    if (
      lowerMsg.includes('trace') ||
      lowerMsg.includes('coverage') ||
      lowerMsg.includes('verification')
    ) {
      return this._handleTraceabilityRequest(mergedCtx, modelState, phase, completenessScore);
    }

    // Default response
    return this._handleDefaultResponse(mergedCtx, analysis, modelState, phase, completenessScore);
  }

  private _handleSuggestionsRequest(
    ctx: ModelContext,
    analysis: ReturnType<SysmlApi['_analyzeSourceText']>,
    modelState: SysmlModelState | undefined,
    phase: ChatPhase,
    completenessScore: number,
  ): ChatResponse {
    const suggestions: string[] = [];
    const validationIssues: string[] = [];
    let suggestedCode: string | undefined;

    if (ctx.partCount === 0) {
      suggestions.push('Define part definitions to describe your system structure.');
      validationIssues.push('No part definitions found in the model.');
      suggestedCode = `part def Controller {\n  doc /* Main system controller */\n  attribute mode : String;\n}\n\npart def Sensor {\n  doc /* Environmental sensor subsystem */\n  attribute sampleRate : Real;\n  port dataOut : DataPort;\n}`;
    }

    if (ctx.requirementCount === 0) {
      suggestions.push('Add requirement definitions to specify what the system shall do.');
      validationIssues.push('No requirements defined. Requirements drive the design.');
      if (!suggestedCode) {
        suggestedCode = `requirement def PerformanceReq {\n  doc /* The system shall respond within 100 milliseconds. */\n}\n\nrequirement def SafetyReq {\n  doc /* The system shall enter a safe state on failure detection. */\n}`;
      }
    }

    if (ctx.behaviorCount === 0) {
      suggestions.push('Define actions or states to describe system behavior.');
      validationIssues.push('No behavior definitions (actions/states) found.');
    }

    if (ctx.verificationCount === 0 && ctx.requirementCount > 0) {
      suggestions.push('Add verification cases for your requirements.');
      validationIssues.push('Requirements exist but have no verification cases.');
      if (!suggestedCode) {
        const reqName = analysis.requirementNames[0] ?? 'PerformanceReq';
        suggestedCode = `verification def ${reqName}Test {\n  doc /* Verify ${reqName} via automated testing. */\n  subject : ${reqName};\n}`;
      }
    }

    if (ctx.portCount === 0 && ctx.partCount > 1) {
      suggestions.push('Define ports to establish interfaces between parts.');
    }

    if (ctx.riskCount === 0) {
      suggestions.push('Identify risks and mitigations for the system.');
    }

    if (suggestions.length === 0) {
      return {
        message:
          'Your model looks well-structured! Consider adding more detailed attributes, ' +
          'constraint blocks, or additional verification cases for deeper analysis.\n\n' +
          `Current model: ${ctx.partCount} parts, ${ctx.requirementCount} requirements, ` +
          `${ctx.behaviorCount} behaviors, ${ctx.verificationCount} verification cases.`,
        phase: 'ready',
        completenessScore: Math.max(completenessScore, 70),
        suggestions: [
          'Add constraint definitions',
          'Define port connections',
          'Generate full SysML output',
        ],
        modelState,
        quality: modelState ? this._assessRequirementQuality(modelState) : undefined,
        traceability: modelState ? this._computeTraceability(modelState) : undefined,
      };
    }

    const message =
      `Based on your model with ${ctx.partCount} parts, ${ctx.requirementCount} requirements, ` +
      `and ${ctx.errorCount} parse errors, here are suggestions:\n\n` +
      suggestions.map((s, i) => `${i + 1}. ${s}`).join('\n');

    return {
      message,
      phase,
      suggestedCode,
      completenessScore,
      suggestions: [
        'Add a requirement',
        'Add a part definition',
        'Add a verification case',
        'Analyze quality',
      ],
      validationIssues,
      modelState,
      quality: modelState ? this._assessRequirementQuality(modelState) : undefined,
      traceability: modelState ? this._computeTraceability(modelState) : undefined,
      contextSummary: `Model has ${ctx.partCount} parts, ${ctx.requirementCount} requirements. ${suggestions.length} suggestions pending.`,
    };
  }

  private _handleGenerateRequest(
    lowerMsg: string,
    ctx: ModelContext,
    analysis: ReturnType<SysmlApi['_analyzeSourceText']>,
    modelState: SysmlModelState | undefined,
    phase: ChatPhase,
    completenessScore: number,
  ): ChatResponse {
    // Generate requirement
    if (lowerMsg.includes('requirement')) {
      const existingNames = analysis.requirementNames;
      const nextNum = existingNames.length + 1;
      const id = `REQ_${String(nextNum).padStart(3, '0')}`;

      const code = `requirement def ${id} {\n  doc /* The system shall [describe behavior]. */\n  @meta { priority: "medium" }\n}`;

      return {
        message: `Here is a requirement definition template. Customize the text to describe what the system shall do:\n\n- Use "shall" for mandatory requirements\n- Be specific and measurable\n- Each requirement should be atomic (one concern)`,
        phase,
        suggestedCode: code,
        suggestedSourceText: code,
        completenessScore,
        suggestions: [
          'Add another requirement',
          'Add a verification case for this requirement',
          'Trace this requirement to a part',
        ],
        modelState,
      };
    }

    // Generate part
    if (lowerMsg.includes('part') || lowerMsg.includes('component')) {
      const existingNames = analysis.partNames;
      const name = this._generateUniqueName('Subsystem', existingNames);

      const code = `part def ${name} {\n  doc /* [Describe the component's responsibility] */\n  attribute status : String;\n  port dataIn : DataPort;\n  port dataOut : DataPort;\n}`;

      return {
        message: `Here is a part definition with ports for inter-component communication. Rename and customize it:`,
        phase,
        suggestedCode: code,
        suggestedSourceText: code,
        completenessScore,
        suggestions: [
          'Add attributes to this part',
          'Connect this part to others',
          'Add a requirement for this part',
        ],
        modelState,
      };
    }

    // Generate behavior (action or state)
    if (
      lowerMsg.includes('action') ||
      lowerMsg.includes('behavior') ||
      lowerMsg.includes('state')
    ) {
      const isState = lowerMsg.includes('state');
      if (isState) {
        const code = `state def OperationalState {\n  doc /* System operational state machine */\n  \n  state def initial IdleState {\n    doc /* Waiting for activation */\n    entry initialize;\n  }\n  \n  state def ActiveState {\n    doc /* Processing inputs */\n  }\n  \n  state def final ShutdownState {\n    doc /* Graceful shutdown */\n  }\n  \n  transition idle_to_active\n    first IdleState\n    then ActiveState;\n  \n  transition active_to_shutdown\n    first ActiveState\n    then ShutdownState;\n}`;

        return {
          message: 'Here is a state machine with initial, active, and shutdown states:',
          phase,
          suggestedCode: code,
          suggestedSourceText: code,
          completenessScore,
          suggestions: ['Add more states', 'Add transition guards', 'Link to a part'],
          modelState,
        };
      }

      const code = `action def ProcessData {\n  doc /* Processes incoming sensor data */\n  in rawData : SensorReading;\n  out processedData : FilteredReading;\n}`;

      return {
        message: 'Here is an action definition with typed inputs and outputs:',
        phase,
        suggestedCode: code,
        suggestedSourceText: code,
        completenessScore,
        suggestions: ['Add more actions', 'Chain actions in a sequence', 'Add constraints'],
        modelState,
      };
    }

    // Generate verification
    if (lowerMsg.includes('verification') || lowerMsg.includes('verify') || lowerMsg.includes('test')) {
      const reqName = analysis.requirementNames[0] ?? 'REQ_001';
      const code = `verification def ${reqName}Test {\n  doc /* Verify ${reqName} through automated testing. */\n  subject : ${reqName};\n}`;

      return {
        message: `Here is a verification case for ${reqName}. The subject references the requirement being verified:`,
        phase,
        suggestedCode: code,
        suggestedSourceText: code,
        completenessScore,
        suggestions: [
          'Add verification for other requirements',
          'Check verification coverage',
          'Analyze traceability',
        ],
        modelState,
      };
    }

    // Generic generate
    return {
      message:
        'I can generate SysML elements for you. What would you like to create?\n\n' +
        '- **Requirement**: "Add a requirement"\n' +
        '- **Part/Component**: "Add a part"\n' +
        '- **Behavior**: "Add an action" or "Add a state machine"\n' +
        '- **Verification**: "Add a verification case"\n' +
        '- **Connection**: "Connect parts together"',
      phase,
      completenessScore,
      suggestions: [
        'Add a requirement',
        'Add a part definition',
        'Add an action',
        'Add a verification case',
      ],
      modelState,
    };
  }

  private _handleQualityRequest(
    ctx: ModelContext,
    analysis: ReturnType<SysmlApi['_analyzeSourceText']>,
    modelState: SysmlModelState | undefined,
    phase: ChatPhase,
    completenessScore: number,
  ): ChatResponse {
    const quality = modelState
      ? this._assessRequirementQuality(modelState)
      : undefined;
    const traceability = modelState
      ? this._computeTraceability(modelState)
      : undefined;

    const qualityScore = quality?.overallScore ?? 0;
    const reqCount = modelState?.requirements?.length ?? ctx.requirementCount;

    let qualityReport = `Model Quality Analysis:\n\n`;
    qualityReport += `  Parts: ${ctx.partCount}\n`;
    qualityReport += `  Requirements: ${reqCount}\n`;
    qualityReport += `  Behaviors: ${ctx.behaviorCount}\n`;
    qualityReport += `  Verification Cases: ${ctx.verificationCount}\n`;
    qualityReport += `  Parse Errors: ${ctx.errorCount}\n`;
    qualityReport += `  Warnings: ${ctx.warningCount}\n\n`;
    qualityReport += `  Completeness Score: ${completenessScore}%\n`;

    if (quality && reqCount > 0) {
      qualityReport += `  INCOSE Quality Score: ${qualityScore}%\n\n`;
      qualityReport += `Requirement Quality (INCOSE Assessment):\n`;

      for (const [name, reqScore] of Object.entries(quality.requirementScores)) {
        qualityReport += `\n  ${name}: ${reqScore.score}/100\n`;
        for (const issue of reqScore.issues) {
          qualityReport += `    [${issue.severity}] ${issue.message}\n`;
        }
      }
    }

    if (traceability && traceability.totalRequirements > 0) {
      qualityReport += `\nTraceability:\n`;
      qualityReport += `  Trace Coverage: ${Math.round((traceability.tracedRequirements / traceability.totalRequirements) * 100)}%\n`;
      qualityReport += `  Verification Coverage: ${traceability.verificationCoverage}%\n`;
      qualityReport += `  Untraced Requirements: ${traceability.untracedRequirements}\n`;
    }

    return {
      message: qualityReport,
      phase,
      completenessScore,
      quality,
      traceability,
      modelState,
      suggestions: [
        'Improve requirement quality',
        'Add verification cases',
        'Trace requirements to parts',
        'Generate full SysML output',
      ],
    };
  }

  private _handleTraceabilityRequest(
    ctx: ModelContext,
    modelState: SysmlModelState | undefined,
    phase: ChatPhase,
    completenessScore: number,
  ): ChatResponse {
    const traceability = modelState
      ? this._computeTraceability(modelState)
      : undefined;

    if (!traceability || traceability.totalRequirements === 0) {
      return {
        message:
          'No requirements found to analyze traceability. Add requirements first, then trace them to parts and add verification cases.',
        phase,
        completenessScore,
        suggestions: ['Add a requirement', 'What is traceability?'],
        modelState,
      };
    }

    let report = `Traceability Analysis:\n\n`;
    report += `  Total Requirements: ${traceability.totalRequirements}\n`;
    report += `  Traced to Parts: ${traceability.tracedRequirements} (${Math.round((traceability.tracedRequirements / traceability.totalRequirements) * 100)}%)\n`;
    report += `  Verification Coverage: ${traceability.verificationCoverage}%\n`;
    report += `  Untraced: ${traceability.untracedRequirements}\n\n`;

    report += `Requirement Trace Details:\n`;
    for (const entry of traceability.entries) {
      const statusIcon =
        entry.status === 'fully-traced'
          ? '[OK]'
          : entry.status === 'partially-traced'
            ? '[PARTIAL]'
            : '[MISSING]';
      report += `\n  ${statusIcon} ${entry.requirementName}\n`;
      report += `    Traced to: ${entry.tracedToParts.length > 0 ? entry.tracedToParts.join(', ') : 'none'}\n`;
      report += `    Verified by: ${entry.verifiedBy.length > 0 ? entry.verifiedBy.join(', ') : 'none'}\n`;
    }

    return {
      message: report,
      phase,
      completenessScore,
      traceability,
      modelState,
      suggestions: [
        'Trace untraced requirements',
        'Add verification cases',
        'Improve quality scores',
      ],
    };
  }

  private _handleDefaultResponse(
    ctx: ModelContext,
    analysis: ReturnType<SysmlApi['_analyzeSourceText']>,
    modelState: SysmlModelState | undefined,
    phase: ChatPhase,
    completenessScore: number,
  ): ChatResponse {
    const quality = modelState
      ? this._assessRequirementQuality(modelState)
      : undefined;
    const traceability = modelState
      ? this._computeTraceability(modelState)
      : undefined;

    let summary = `I can help you with your SysML model. `;
    summary += `Current model: ${ctx.partCount} parts, ${ctx.requirementCount} requirements`;

    if (ctx.behaviorCount > 0) summary += `, ${ctx.behaviorCount} behaviors`;
    if (ctx.verificationCount > 0) summary += `, ${ctx.verificationCount} verification cases`;
    if (ctx.errorCount > 0) summary += ` (${ctx.errorCount} parse errors)`;
    summary += `.\n\nTry asking me to:\n`;
    summary += `- Suggest improvements for your model\n`;
    summary += `- Generate a new requirement, part, or action\n`;
    summary += `- Analyze model quality (INCOSE assessment)\n`;
    summary += `- Check traceability and verification coverage\n`;
    summary += `- Add verification cases for requirements`;

    return {
      message: summary,
      phase,
      completenessScore,
      suggestions: [
        'Suggest improvements',
        'Analyze quality',
        'Check traceability',
        'Generate a requirement',
      ],
      quality,
      traceability,
      modelState,
      contextSummary: `Model: ${ctx.partCount} parts, ${ctx.requirementCount} requirements, ${ctx.behaviorCount} behaviors.`,
    };
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Internal: SysML generation from model state
  // ══════════════════════════════════════════════════════════════════════════

  private _generateSysMLFromState(
    state: SysmlModelState,
    modelName: string,
  ): GenerateResponse {
    const safeName = modelName.replace(/[^A-Za-z0-9_]/g, '') || 'SystemModel';
    const outputs: SysmlSuggestedOutput[] = [];

    // --- Main package file ---
    let mainFile = `package ${safeName} {\n\n`;

    // Parts
    const parts = state.parts ?? [];
    if (parts.length > 0) {
      mainFile += `  // --- Part Definitions ---\n`;
      for (const part of parts) {
        mainFile += `  part def ${part.name} {\n`;
        if (part.description) {
          mainFile += `    doc /* ${part.description} */\n`;
        }
        for (const attr of part.attributes ?? []) {
          mainFile += `    attribute ${attr} : String;\n`;
        }
        for (const port of part.ports ?? []) {
          mainFile += `    port ${port} : DataPort;\n`;
        }
        mainFile += `  }\n\n`;
      }
    }

    // Behaviors
    const behaviors = state.behaviors ?? [];
    if (behaviors.length > 0) {
      mainFile += `  // --- Behavior Definitions ---\n`;
      for (const beh of behaviors) {
        const keyword = beh.type === 'state' ? 'state def' : 'action def';
        mainFile += `  ${keyword} ${beh.name} {\n`;
        if (beh.description) {
          mainFile += `    doc /* ${beh.description} */\n`;
        }
        for (const inp of beh.inputs ?? []) {
          mainFile += `    in ${inp} : DataType;\n`;
        }
        for (const out of beh.outputs ?? []) {
          mainFile += `    out ${out} : DataType;\n`;
        }
        mainFile += `  }\n\n`;
      }
    }

    mainFile += `}\n`;

    outputs.push({
      filename: `${safeName}.sysml`,
      content: mainFile,
      description: `Main package with ${parts.length} parts and ${behaviors.length} behaviors`,
    });

    // --- Requirements file ---
    const reqs = state.requirements ?? [];
    if (reqs.length > 0) {
      let reqFile = `package ${safeName}Requirements {\n\n`;
      reqFile += `  // --- Requirements ---\n`;
      for (const req of reqs) {
        reqFile += `  requirement def ${req.name} {\n`;
        reqFile += `    doc /* ${req.text} */\n`;
        const meta: string[] = [];
        if (req.priority) meta.push(`priority: "${req.priority}"`);
        if (req.source) meta.push(`source: "${req.source}"`);
        if (meta.length > 0) {
          reqFile += `    @meta { ${meta.join(', ')} }\n`;
        }
        if (req.rationale) {
          reqFile += `    /* Rationale: ${req.rationale} */\n`;
        }
        reqFile += `  }\n\n`;
      }

      // Satisfy relationships
      for (const req of reqs) {
        for (const part of req.tracedTo ?? []) {
          reqFile += `  satisfy ${req.name} by ${part};\n`;
        }
      }

      reqFile += `}\n`;

      outputs.push({
        filename: `${safeName}Requirements.sysml`,
        content: reqFile,
        description: `${reqs.length} requirement definitions with traceability`,
      });
    }

    // --- Verification file ---
    const verifications = state.verificationCases ?? [];
    if (verifications.length > 0) {
      let verFile = `package ${safeName}Verification {\n\n`;
      verFile += `  // --- Verification Cases ---\n`;
      for (const vc of verifications) {
        verFile += `  verification def ${vc.name} {\n`;
        if (vc.description) {
          verFile += `    doc /* ${vc.description} */\n`;
        }
        for (const reqName of vc.verifies ?? []) {
          verFile += `    subject : ${reqName};\n`;
        }
        verFile += `  }\n\n`;
      }
      verFile += `}\n`;

      outputs.push({
        filename: `${safeName}Verification.sysml`,
        content: verFile,
        description: `${verifications.length} verification cases`,
      });
    }

    // --- Risks file (as comments/metadata) ---
    const risks = state.risks ?? [];
    if (risks.length > 0) {
      let riskFile = `package ${safeName}Risks {\n\n`;
      riskFile += `  // --- Risk Register ---\n`;
      for (const risk of risks) {
        riskFile += `  /* Risk: ${risk.name}\n`;
        riskFile += `   * Severity: ${risk.severity ?? 'medium'}\n`;
        riskFile += `   * Description: ${risk.description}\n`;
        if (risk.mitigation) {
          riskFile += `   * Mitigation: ${risk.mitigation}\n`;
        }
        riskFile += `   */\n\n`;
      }
      riskFile += `}\n`;

      outputs.push({
        filename: `${safeName}Risks.sysml`,
        content: riskFile,
        description: `${risks.length} risk entries`,
      });
    }

    // Combined source text
    const suggestedSourceText = outputs.map((o) => o.content).join('\n\n');

    return { suggestedSourceText, suggestedOutputs: outputs };
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Internal: build model state from parsed source analysis
  // ══════════════════════════════════════════════════════════════════════════

  private _buildModelStateFromAnalysis(
    analysis: ReturnType<SysmlApi['_analyzeSourceText']>,
  ): SysmlModelState {
    const state: SysmlModelState = {};

    if (analysis.partNames.length > 0) {
      state.parts = analysis.partNames.map((name) => ({ name }));
    }

    if (analysis.requirementNames.length > 0) {
      state.requirements = analysis.requirementNames.map((name) => ({
        name,
        text: analysis.requirementTexts.get(name) ?? '',
      }));
    }

    if (analysis.behaviorNames.length > 0) {
      state.behaviors = analysis.behaviorNames.map((name) => ({ name }));
    }

    if (analysis.verificationNames.length > 0) {
      state.verificationCases = analysis.verificationNames.map((name) => ({
        name,
        method: 'test' as const,
      }));
    }

    return state;
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Internal: quality score (legacy interface, still used by refreshQualityScore)
  // ══════════════════════════════════════════════════════════════════════════

  private _generateQualityScore(ctx: ModelContext): QualityScore {
    const completeness = this._computeCompletenessFromContext(ctx);
    const issues: QualityIssue[] = [];

    if (ctx.partCount === 0) {
      issues.push({
        category: 'Structure',
        severity: 'error',
        message: 'No part definitions found.',
        suggestion: 'Add at least one part def to define system structure.',
      });
    }
    if (ctx.requirementCount === 0) {
      issues.push({
        category: 'Requirements',
        severity: 'warning',
        message: 'No requirements defined.',
        suggestion: 'Add requirement definitions to specify system needs.',
      });
    }
    if (ctx.behaviorCount === 0) {
      issues.push({
        category: 'Behavior',
        severity: 'info',
        message: 'No behaviors (actions/states) defined.',
        suggestion: 'Add action or state definitions to model dynamic behavior.',
      });
    }
    if (ctx.verificationCount === 0) {
      issues.push({
        category: 'Verification',
        severity: 'warning',
        message: 'No verification cases.',
        suggestion: 'Add verification cases to validate requirements.',
      });
    }
    if (ctx.errorCount > 0) {
      issues.push({
        category: 'Syntax',
        severity: 'error',
        message: `${ctx.errorCount} parse error(s) detected.`,
        suggestion: 'Fix syntax errors for a valid model.',
      });
    }
    if (ctx.portCount === 0 && ctx.partCount > 1) {
      issues.push({
        category: 'Interfaces',
        severity: 'info',
        message: 'No ports defined for inter-part communication.',
        suggestion: 'Add port definitions to define system interfaces.',
      });
    }

    return {
      overall: completeness,
      completeness,
      consistency: ctx.errorCount === 0 ? 90 : Math.max(20, 90 - ctx.errorCount * 15),
      clarity: ctx.partCount > 0 && ctx.requirementCount > 0 ? 80 : 40,
      issues,
    };
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Internal: helpers
  // ══════════════════════════════════════════════════════════════════════════

  private _generateUniqueName(base: string, existing: string[]): string {
    if (!existing.includes(base)) return base;
    let i = 2;
    while (existing.includes(`${base}${i}`)) i++;
    return `${base}${i}`;
  }
}

export function createSysmlApi(config: SysmlApiConfig): SysmlApi {
  return new SysmlApi(config);
}
